import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Floating bottom-right CTA that stays visible as the user scrolls a sales page.
 *
 * Appears once the user has scrolled past the hero (default ~320px) so it
 * doesn't compete with the page's primary above-the-fold CTA.
 *
 * Usage (navigation):
 *   <StickyCTA label="Join Now" href="/the-menopause-invitation-live" testId="..." />
 *
 * Usage (action — e.g. open Stripe checkout):
 *   <StickyCTA label="Join Now" onClick={startCheckout} busy={busy} testId="..." />
 *
 * Props:
 *   - label        Button text (default: "Join Now")
 *   - href         Internal route OR external URL — used when onClick is not provided
 *   - onClick      If provided, renders a <button>; takes precedence over href
 *   - busy         When true (only meaningful with onClick), shows a loading state
 *   - testId       data-testid for QA
 *   - showAfter    Pixels of scroll before it appears (default: 320)
 *   - tone         "sage" (default) | "gold" — colour accent
 */
export default function StickyCTA({
  label = "Join Now",
  href = "#",
  onClick,
  busy = false,
  testId = "sticky-cta",
  showAfter = 320,
  tone = "sage",
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY >= showAfter);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  const isAnchor = href.startsWith("#");
  const isExternal = /^https?:\/\//.test(href);
  const colour =
    tone === "gold"
      ? "bg-gold hover:bg-gold-dark text-ink"
      : tone === "vibrant"
      ? "bg-vibrant hover:bg-vibrant-dark text-cream"
      : "bg-sage hover:bg-sage-dark text-cream";

  const baseClass = `inline-flex items-center gap-2.5 ${colour} disabled:opacity-60 rounded-full px-7 md:px-8 py-4 md:py-4 font-bold tracking-wide text-sm md:text-base shadow-[0_18px_45px_-12px_rgba(61,176,122,0.6)] hover:shadow-[0_22px_55px_-12px_rgba(61,176,122,0.7)] transition-all`;

  const Inner = (
    <>
      {label}
      <ArrowRight size={15} strokeWidth={1.8} />
    </>
  );

  return (
    <div
      className={`fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      data-testid={testId}
    >
      {onClick ? (
        <button type="button" onClick={onClick} disabled={busy} className={baseClass}>
          {busy ? "Opening…" : Inner}
        </button>
      ) : isAnchor || isExternal ? (
        <a
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className={baseClass}
        >
          {Inner}
        </a>
      ) : (
        <Link to={href} className={baseClass}>
          {Inner}
        </Link>
      )}
    </div>
  );
}
